<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import Dialog from 'primevue/dialog'
  import Button from 'primevue/button'
  import InputText from 'primevue/inputtext'
  import Checkbox from 'primevue/checkbox'
  import Tag from 'primevue/tag'
  import ConfirmDialog from 'primevue/confirmdialog'
  import { useConfirm } from 'primevue/useconfirm'
  import { useRapportShares, type InstallateurShare, type OpdrachtgeverShare } from '../composables/useRapportShares'

  const props = defineProps<{
    visible: boolean
    rapportId: string
    shareType: 'opdrachtgever' | 'installateur'
  }>()

  const emit = defineEmits<{
    'update:visible': [value: boolean]
  }>()

  const confirm = useConfirm()
  const {
    shares,
    loading,
    sharing,
    fetchShares,
    shareRapport,
    updateOpdrachtgeverRoles,
    revokeOpdrachtgever,
    revokeInstallateur,
  } = useRapportShares(props.rapportId)

  const email = ref('')
  const isBeheerder = ref(true)
  const isEigenaar = ref(false)
  const includeOfferte = ref(false)
  const emailTouched = ref(false)

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  const isOpdrachtgeverShare = computed(() => props.shareType === 'opdrachtgever')
  const dialogTitle = computed(() =>
    isOpdrachtgeverShare.value ? 'Delen met opdrachtgever' : 'Delen met installateur',
  )
  const emailValid = computed(() => EMAIL_REGEX.test(email.value.trim()))
  const rolesValid = computed(() => !isOpdrachtgeverShare.value || isBeheerder.value || isEigenaar.value)
  const canSubmit = computed(() => emailValid.value && rolesValid.value && !sharing.value)

  watch(
    () => props.visible,
    (visible) => {
      if (visible) {
        email.value = ''
        emailTouched.value = false
        isBeheerder.value = true
        isEigenaar.value = false
        includeOfferte.value = false
        fetchShares()
      }
    },
  )

  const submit = async () => {
    emailTouched.value = true
    if (!canSubmit.value) return
    const result = await shareRapport({
      shareType: props.shareType,
      email: email.value.trim(),
      isBeheerder: isOpdrachtgeverShare.value ? isBeheerder.value : undefined,
      isEigenaar: isOpdrachtgeverShare.value ? isEigenaar.value : undefined,
      includeOfferte: isOpdrachtgeverShare.value ? includeOfferte.value : undefined,
    })
    if (result?.success && !result.already_shared) {
      email.value = ''
      emailTouched.value = false
    }
  }

  const resendOpdrachtgeverInvite = (share: OpdrachtgeverShare) => {
    shareRapport({
      shareType: 'opdrachtgever',
      email: share.email,
      resend: true,
      isBeheerder: share.is_beheerder,
      isEigenaar: share.is_eigenaar,
      includeOfferte: share.offerte_gedeeld,
    })
  }

  const resendInstallateurInvite = (share: InstallateurShare) => {
    if (!share.email) return
    shareRapport({ shareType: 'installateur', email: share.email, resend: true })
  }

  const confirmRevokeOpdrachtgever = (share: OpdrachtgeverShare) => {
    confirm.require({
      group: 'rapportShareRevoke',
      message: `Weet u zeker dat u de toegang van ${share.naam || share.email} wilt intrekken?`,
      header: 'Toegang intrekken',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger',
      accept: () => revokeOpdrachtgever(share.opdrachtgever_id),
    })
  }

  const confirmRevokeInstallateur = (share: InstallateurShare) => {
    confirm.require({
      group: 'rapportShareRevoke',
      message: `Weet u zeker dat u de toegang van ${share.naam || share.email} wilt intrekken?`,
      header: 'Toegang intrekken',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger',
      accept: () => revokeInstallateur(share.installateur_id),
    })
  }

  const toggleRole = (share: OpdrachtgeverShare, role: 'is_beheerder' | 'is_eigenaar') => {
    const next = {
      is_beheerder: share.is_beheerder,
      is_eigenaar: share.is_eigenaar,
      [role]: !share[role],
    }
    if (!next.is_beheerder && !next.is_eigenaar) return
    updateOpdrachtgeverRoles(share.opdrachtgever_id, next)
  }
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(val: boolean) => emit('update:visible', val)"
    modal
    :draggable="false"
    class="w-full max-w-xl"
    :header="dialogTitle"
  >
    <ConfirmDialog group="rapportShareRevoke" />

    <form
      class="flex flex-col gap-4"
      @submit.prevent="submit"
    >
      <div class="flex flex-col gap-1">
        <label
          for="share-email"
          class="text-sm font-medium"
          >E-mailadres</label
        >
        <InputText
          id="share-email"
          v-model="email"
          type="email"
          placeholder="naam@voorbeeld.nl"
          :invalid="emailTouched && !emailValid"
          @blur="emailTouched = true"
        />
        <small
          v-if="emailTouched && !emailValid"
          class="text-red-500"
          >Vul een geldig e-mailadres in</small
        >
        <small class="text-background-500">
          {{
            isOpdrachtgeverShare
              ? 'Heeft deze persoon nog geen account, dan sturen we automatisch een uitnodiging.'
              : 'De installateur krijgt leestoegang tot dit rapport en kan herstelpunten indienen.'
          }}
        </small>
      </div>

      <div
        v-if="isOpdrachtgeverShare"
        class="flex flex-col gap-2"
      >
        <span class="text-sm font-medium">Rol</span>
        <div class="flex items-center gap-6">
          <label class="flex items-center gap-2">
            <Checkbox
              v-model="isBeheerder"
              :binary="true"
            />
            <span>Beheerder</span>
          </label>
          <label class="flex items-center gap-2">
            <Checkbox
              v-model="isEigenaar"
              :binary="true"
            />
            <span>Eigenaar</span>
          </label>
        </div>
        <small
          v-if="!rolesValid"
          class="text-red-500"
          >Kies minimaal één rol</small
        >
        <label class="mt-1 flex items-center gap-2">
          <Checkbox
            v-model="includeOfferte"
            :binary="true"
          />
          <span>Ook offerte delen (incl. prijzen)</span>
        </label>
      </div>

      <div class="flex justify-end">
        <Button
          type="submit"
          label="Delen"
          icon="pi pi-share-alt"
          :loading="sharing"
          :disabled="!canSubmit"
        />
      </div>
    </form>

    <div class="mt-6 border-t pt-4">
      <h3 class="mb-3 text-sm font-semibold">Huidige toegang</h3>

      <p
        v-if="loading"
        class="text-background-500 text-sm"
      >
        Toegang laden...
      </p>

      <template v-else>
        <div
          v-if="isOpdrachtgeverShare"
          class="flex flex-col gap-2"
        >
          <p
            v-if="shares.opdrachtgevers.length === 0"
            class="text-background-500 text-sm"
          >
            Dit rapport is nog niet met andere opdrachtgevers gedeeld.
          </p>
          <div
            v-for="share in shares.opdrachtgevers"
            :key="share.opdrachtgever_id"
            class="border-surface flex items-center justify-between gap-3 rounded-lg border p-3"
          >
            <div class="min-w-0">
              <p class="truncate font-medium">{{ share.naam || share.email }}</p>
              <p class="text-background-500 truncate text-sm">{{ share.email }}</p>
              <div class="mt-1 flex flex-wrap items-center gap-1">
                <Tag
                  :severity="share.is_beheerder ? 'info' : 'secondary'"
                  value="Beheerder"
                  class="cursor-pointer"
                  @click="toggleRole(share, 'is_beheerder')"
                />
                <Tag
                  :severity="share.is_eigenaar ? 'success' : 'secondary'"
                  value="Eigenaar"
                  class="cursor-pointer"
                  @click="toggleRole(share, 'is_eigenaar')"
                />
                <Tag
                  v-if="share.offerte_gedeeld"
                  severity="warn"
                  value="Incl. offerte"
                />
                <Tag
                  v-if="!share.profile_completed"
                  severity="secondary"
                  value="Nog niet ingelogd"
                />
              </div>
            </div>
            <div class="flex flex-none items-center gap-1">
              <Button
                v-if="!share.profile_completed"
                icon="pi pi-envelope"
                severity="secondary"
                text
                rounded
                aria-label="Opnieuw uitnodigen"
                v-tooltip.bottom="'Opnieuw uitnodigen'"
                :disabled="sharing"
                @click="resendOpdrachtgeverInvite(share)"
              />
              <Button
                icon="pi pi-trash"
                severity="danger"
                text
                rounded
                aria-label="Toegang intrekken"
                @click="confirmRevokeOpdrachtgever(share)"
              />
            </div>
          </div>
        </div>

        <div
          v-else
          class="flex flex-col gap-2"
        >
          <p
            v-if="shares.installateurs.length === 0"
            class="text-background-500 text-sm"
          >
            Dit rapport is nog niet met installateurs gedeeld.
          </p>
          <div
            v-for="share in shares.installateurs"
            :key="share.installateur_id"
            class="border-surface flex items-center justify-between gap-3 rounded-lg border p-3"
          >
            <div class="min-w-0">
              <p class="truncate font-medium">{{ share.naam }}</p>
              <p class="text-background-500 truncate text-sm">
                {{ share.bedrijfsnaam ? `${share.bedrijfsnaam} · ` : '' }}{{ share.email || 'Geen e-mailadres' }}
              </p>
              <div class="mt-1 flex flex-wrap items-center gap-1">
                <Tag
                  v-if="!share.has_account || !share.profile_completed"
                  severity="secondary"
                  value="Nog niet ingelogd"
                />
              </div>
            </div>
            <div class="flex flex-none items-center gap-1">
              <Button
                v-if="share.email && (!share.has_account || !share.profile_completed)"
                icon="pi pi-envelope"
                severity="secondary"
                text
                rounded
                aria-label="Opnieuw uitnodigen"
                v-tooltip.bottom="'Opnieuw uitnodigen'"
                :disabled="sharing"
                @click="resendInstallateurInvite(share)"
              />
              <Button
                icon="pi pi-trash"
                severity="danger"
                text
                rounded
                aria-label="Toegang intrekken"
                @click="confirmRevokeInstallateur(share)"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </Dialog>
</template>
